/**
 * flexible_frame schema shared with mobile (3×3 regions, ratios in cell / image space).
 */

export type FlexibleFrameRatios = {
  left_ratio: number;
  right_ratio: number;
  top_ratio: number;
  bottom_ratio: number;
  scale_ratio: number;
  /** width / height of the axis-aligned bbox used when encoding */
  aspect_ratio: number;
  region: number;
  center_x_ratio: number;
  center_y_ratio: number;
  // EWW-437: Stable-Axis Responsive Scaling. `scale_ratio`/`aspect_ratio` above derive size from
  // `min(regionW, regionH)`, a single scalar mixing width and height -- when the canvas aspect
  // ratio changes between save and load, that scalar shifts non-proportionally and watermarks
  // resize/overlap incorrectly. These six fields store width and height ratios SEPARATELY
  // (against the canvas's own width/height, not a min() of the two), plus which axis the
  // watermark's own content should scale with, so repeated aspect-ratio changes stay reversible.
  // All optional and additive: old templates lack them and keep using the legacy fields above
  // unchanged (see `getEdgedFrameValueFromFlexibleFrame`'s `hasStableAxis` branch).
  /** Rotated bounding-box width/height as ratios of canvas width/height (position-only, replaces `scale_ratio` for bbox sizing -- does not affect actual render size). */
  bounding_width_ratio?: number;
  bounding_height_ratio?: number;
  /** Unrotated content width/height as ratios of canvas width/height (drives actual render size). */
  content_width_ratio?: number;
  content_height_ratio?: number;
  /** content_width / content_height, used with `size_axis` to derive the other dimension. */
  content_aspect_ratio?: number;
  /** Which axis drives scaling: 0 = width-driven ("wide" content), 1 = height-driven ("tall" content). */
  size_axis?: 0 | 1;
};

/** Optional ios_* fields used in-memory before API strip (web + admin save paths). */
export type FlexibleFrameWithIos = FlexibleFrameRatios & {
  ios_height?: number;
  ios_width?: number;
};

export type ImageFrame = { width: number; height: number };

export type WatermarkRect = { x: number; y: number; width: number; height: number };

/**
 * Encode flexible_frame from an axis-aligned bbox (e.g. rotated watermark AABB) in image space.
 * Matches web `getFlexibleEdgedFrameValue`: 3×3 cell ratios + edge pixel tolerance on encode.
 */
export function getFlexibleEdgedFrameValue(
  watermarkFrame: WatermarkRect,
  imageFrame: ImageFrame
): FlexibleFrameRatios {
  const imageW = imageFrame.width;
  const imageH = imageFrame.height;
  const regionW = imageW / 3;
  const regionH = imageH / 3;

  const wmLeft = watermarkFrame.x;
  const wmTop = watermarkFrame.y;
  const wmRight = wmLeft + watermarkFrame.width;
  const wmBottom = wmTop + watermarkFrame.height;

  const watermarkCenterX = wmLeft + watermarkFrame.width / 2;
  const watermarkCenterY = wmTop + watermarkFrame.height / 2;

  // Which of the 3x3 grid cells the watermark's centre falls in (0-8, left-to-right top-to-bottom).
  // Only clamped on the upper end -- a centre past the right/bottom edge stays in the last column/
  // row, but one before the left/top edge (negative) is NOT clamped, so `region` can end up
  // negative for a watermark dragged off the top-left. Decode handles that out-of-range case.
  const col = Math.min(Math.floor(watermarkCenterX / regionW), 2);
  const row = Math.min(Math.floor(watermarkCenterY / regionH), 2);
  const region = row * 3 + col;

  const regionLeft = col * regionW;
  const regionTop = row * regionH;
  const regionRight = regionLeft + regionW;
  const regionBottom = regionTop + regionH;
  const ref = Math.min(regionW, regionH);

  // A watermark placed almost flush against an edge (within 6px) is treated as EXACTLY flush
  // (ratio 0), so tiny rounding noise on save doesn't leave a barely-visible gap on decode.
  const edgePxTol = 6;
  const edgeRatio = (gapPx: number, axisPx: number) => {
    if (axisPx <= 0) return 0;
    const g = Number(gapPx) || 0;
    if (Math.abs(g) <= edgePxTol) return 0;
    return g / axisPx;
  };

  return {
    left_ratio: edgeRatio(wmLeft - regionLeft, regionW),
    right_ratio: edgeRatio(regionRight - wmRight, regionW),
    top_ratio: edgeRatio(wmTop - regionTop, regionH),
    bottom_ratio: edgeRatio(regionBottom - wmBottom, regionH),
    scale_ratio: watermarkFrame.width / ref,
    aspect_ratio: watermarkFrame.width / Math.max(0.0001, watermarkFrame.height),
    region,
    center_x_ratio: watermarkCenterX / imageW,
    center_y_ratio: watermarkCenterY / imageH,
  };
}

export type FlexibleFrameDecodeInput = {
  left_ratio: number;
  right_ratio: number;
  top_ratio: number;
  bottom_ratio: number;
  scale_ratio: number;
  aspect_ratio: number;
  region?: number;
  center_x_ratio: number;
  center_y_ratio: number;
  // EWW-437: see the matching fields on `FlexibleFrameRatios` above.
  bounding_width_ratio?: number;
  bounding_height_ratio?: number;
  content_width_ratio?: number;
  content_height_ratio?: number;
  content_aspect_ratio?: number;
  size_axis?: 0 | 1;
};

/**
 * Decode flexible_frame to axis-aligned bbox in image space.
 * Matches web `getEdgedFrameValueFromFlexibleFrame` / mobile apply logic.
 */
export function getEdgedFrameValueFromFlexibleFrame(
  flexibleFrame: FlexibleFrameDecodeInput | null | undefined,
  imageSize: ImageFrame | null | undefined
): (WatermarkRect & { contentWidth?: number; contentHeight?: number }) | null {
  if (!flexibleFrame || !imageSize) {
    return null;
  }

  const imageW = imageSize.width;
  const imageH = imageSize.height;

  const regionW = imageW / 3;
  const regionH = imageH / 3;

  // Matches iOS's `Quadrant.fromValue`: `Quadrant(rawValue: value) ?? .midCenter` -- an
  // out-of-range value falls back to the CENTER bucket (region 4), not the nearest edge.
  // Encode's `col`/`row` only clamp their upper bound (see `getFlexibleEdgedFrameValue`), so a
  // watermark dragged far enough off-canvas produces a `region` outside [0, 8]; the saved
  // `left_ratio`/`top_ratio`/etc. were computed against THAT out-of-grid region's `regionLeft`/
  // `regionTop`, not the nearest in-grid one. Clamping toward the nearest edge quadrant here (the
  // previous `Math.max(0, Math.min(8, ...))`) reused those ratios against the WRONG region
  // origin, pulling the watermark back toward the canvas instead of restoring its true off-canvas
  // position. Falling back to region 4 instead reconstructs from `center_x_ratio`/
  // `center_y_ratio` (unclamped raw ratios computed straight from the true off-canvas center),
  // which is what actually preserves it -- exactly the path iOS's own fallback takes.
  const rawRegion = Number(flexibleFrame.region);
  const region =
    Number.isFinite(rawRegion) && rawRegion >= 0 && rawRegion <= 8
      ? Math.floor(rawRegion)
      : 4;

  const col = region % 3;
  const row = Math.floor(region / 3);

  const regionLeft = col * regionW;
  const regionTop = row * regionH;
  const ref = Math.min(regionW, regionH);

  // Ported directly from iOS's `getEgdedFrameValueFromFlexibleFrame` (FlexibleFrameUtils.swift):
  // single-axis sizing only. `size_axis` (`sizeAxis` on iOS) picks which stored ratio drives the
  // bbox's width; the other dimension is always `width / aspect_ratio`. iOS has neither a
  // separate "content vs bounding" size (our earlier EWW-437 `content_*_ratio` split) nor an
  // aspect-neutral cross-ratio correction (our earlier EWW-462 `sqrt(W*H)` scaling) -- both were
  // web-only divergences. Removed so decode matches the reference implementation exactly; a
  // template now sizes identically on both platforms. `contentWidth`/`contentHeight` are no
  // longer computed here for the same reason (iOS's bbox function does not produce them either);
  // every caller already falls back to `getUnrotatedSizeFromBbox` when they are undefined, which
  // is the bbox-inversion path iOS's own callers use to recover unrotated content size.
  const hasAxisRatios =
    (Number(flexibleFrame.bounding_width_ratio) || 0) > 0 &&
    (Number(flexibleFrame.bounding_height_ratio) || 0) > 0 &&
    (Number(flexibleFrame.aspect_ratio) || 0) > 0;

  const sizeAxis: 0 | 1 = flexibleFrame.size_axis === 1 ? 1 : 0;

  let width: number;
  if (hasAxisRatios) {
    const boundingAspect = Number(flexibleFrame.aspect_ratio) || 1;
    if (sizeAxis === 0) {
      width = (Number(flexibleFrame.bounding_width_ratio) || 0) * imageW;
    } else {
      width = (Number(flexibleFrame.bounding_height_ratio) || 0) * imageH * boundingAspect;
    }
  } else {
    const scaleRatio = Number(flexibleFrame.scale_ratio) || 0;
    width = scaleRatio > 0 ? scaleRatio * ref : 0;
  }
  const height = width / Math.max(0.0001, Number(flexibleFrame.aspect_ratio) || 1);

  let x = 0;
  let y = 0;
  // 9-region anchor switch, matching iOS's `Quadrant` enum 1:1 (both by numeric value and by
  // anchoring rule -- see `getEgdedFrameValueFromFlexibleFrame` in iOS's FlexibleFrameUtils.swift):
  //   0 topLeft     1 topCenter     2 topRight
  //   3 midLeft     4 midCenter     5 midRight
  //   6 bottomLeft  7 bottomCenter  8 bottomRight  (the `default` case)
  // Each axis is anchored independently, so a region name like "topRight" means: X anchored from
  // the RIGHT edge, Y anchored from the TOP edge -- not "anchored to the top-right corner ratio".
  //
  // Per axis, there are exactly three anchoring rules, chosen by column (X) / row (Y):
  //   - LEFT column / TOP row:    edge-anchored from regionLeft/regionTop using left_ratio/top_ratio
  //                               (x = regionLeft + left_ratio * regionW, and similarly for y)
  //   - CENTER column / row:      anchored by the watermark's own centre ratio, then offset back
  //                               by half its size (x = center_x_ratio * imageW - width / 2)
  //   - RIGHT column / BOTTOM row: edge-anchored from the region's FAR edge using
  //                               right_ratio/bottom_ratio, subtracting the watermark's size so
  //                               the ratio measures the gap to that far edge, not to its own origin
  //                               (x = regionLeft + regionW - right_ratio * regionW - width)
  switch (region) {
    case 0: // topLeft: X left-anchored, Y top-anchored
      x = regionLeft + flexibleFrame.left_ratio * regionW;
      y = regionTop + flexibleFrame.top_ratio * regionH;
      break;
    case 1: // topCenter: X centre-anchored, Y top-anchored
      x = flexibleFrame.center_x_ratio * imageW - width / 2;
      y = regionTop + flexibleFrame.top_ratio * regionH;
      break;
    case 2: // topRight: X right-anchored, Y top-anchored
      x = regionLeft + regionW - flexibleFrame.right_ratio * regionW - width;
      y = regionTop + flexibleFrame.top_ratio * regionH;
      break;
    case 3: // midLeft: X left-anchored, Y centre-anchored
      x = regionLeft + flexibleFrame.left_ratio * regionW;
      y = flexibleFrame.center_y_ratio * imageH - height / 2;
      break;
    case 4: // midCenter: X and Y both centre-anchored -- also the out-of-range fallback (see the
      // `region` derivation above), matching iOS's `Quadrant.fromValue(...) ?? .midCenter`.
      x = flexibleFrame.center_x_ratio * imageW - width / 2;
      y = flexibleFrame.center_y_ratio * imageH - height / 2;
      break;
    case 5: // midRight: X right-anchored, Y centre-anchored
      x = regionLeft + regionW - flexibleFrame.right_ratio * regionW - width;
      y = flexibleFrame.center_y_ratio * imageH - height / 2;
      break;
    case 6: // bottomLeft: X left-anchored, Y bottom-anchored
      x = regionLeft + flexibleFrame.left_ratio * regionW;
      y = regionTop + regionH - flexibleFrame.bottom_ratio * regionH - height;
      break;
    case 7: // bottomCenter: X centre-anchored, Y bottom-anchored
      x = flexibleFrame.center_x_ratio * imageW - width / 2;
      y = regionTop + regionH - flexibleFrame.bottom_ratio * regionH - height;
      break;
    default: // bottomRight (region 8): X right-anchored, Y bottom-anchored
      x = regionLeft + regionW - flexibleFrame.right_ratio * regionW - width;
      y = regionTop + regionH - flexibleFrame.bottom_ratio * regionH - height;
      break;
  }

  // `contentWidth`/`contentHeight` are always omitted now -- see the sizing block's doc comment.
  // Every caller checks `!= null` before using them and falls back to inverting rotation off this
  // bbox otherwise, so leaving them out simply routes every caller through that existing path.
  return {
    x,
    y,
    width,
    height,
  };
}
